/**
 * Resume layout guardrails — prevents section overlap / stream-stitch glitches
 * and raw LLM markup (``` fences, [+ SECTION] tags) leaking into the canvas.
 */

export const RESUME_CANONICAL_SECTION_ORDER = [
  'Header',
  'Professional Summary',
  'Work Experience',
  'Technical Skills',
  'Education',
  'Projects',
] as const;

/** Strict output structure — append to all resume section LLM system prompts. */
export const RESUME_OUTPUT_STRUCTURE_DIRECTIVE = `
Universal Standard Resume Generation Rules:
1. Strict Section Hierarchy: Always output sections in this exact order: Header -> Professional Summary -> Professional Experience -> Technical & Core Skills -> Education & Certifications.
2. Header Format Integrity: The header must be a clean, single-block layout: [Full Name] | [Target Role / Senior Title] | [Location] | [Email] | [Phone] | [LinkedIn]. Never insert random fragments, broken sentences, or default marketing taglines (e.g., "Innovative Product Manager...") into the header or summary.
3. Experience Block Consistency: Every work history entry MUST strictly follow this template with a BLANK LINE between entries:

   **[Job Title]** | [Company Name] | [Location]
   [Start Date] – [End Date]
   • [Achievement bullet 1 — starts with power verb, includes metric]
   • [Achievement bullet 2]
   • [Achievement bullet 3]

   [BLANK LINE before next role]

   Rules for each entry:
   - ALWAYS bold the job title with **
   - ALWAYS include the REAL company name (never "Unknown")
   - Include 3-5 action-oriented bullet points per role
   - Each bullet MUST be a complete sentence (no cut-off mid-sentence)
   - Start each bullet with a strong action verb (Led, Spearheaded, Architected, Delivered, etc.)
   - Include quantifiable metrics where possible
4. No Cross-Contamination: Never mix tool lists (like Figma, Jira) into the Work Experience block or paste summary fragments into the skills category.
5. Bullet Completeness: Every bullet point must be a COMPLETE sentence. Never cut off mid-sentence or mid-thought. If the content would be too long, break it into two bullets.`.trim();

/** System-prompt block — append to agent / section-optimize instructions. */
export const RESUME_LAYOUT_GUARDRAILS = `
RESUME LAYOUT RULES (critical — prevents scrambled sections):
1. Generate ONE resume section per LLM call. Never combine Header + Summary + Experience in a single response.
2. Canonical order: Header → Professional Summary → Work Experience → Technical Skills → Education → Projects.
3. Each section is self-contained: bullets stay under their role; no text spillover between headings.
4. Output ONLY the section body as clean plain text — NO code fences, NO [SECTION_NAME] tags, NO \`\`\`markdown wrappers.
5. Return the complete section in one response — no partial streaming, no mid-sentence cutoffs.
6. Blank line between roles and between major headings for vertical separation.

${RESUME_OUTPUT_STRUCTURE_DIRECTIVE}`.trim();

export const RESUME_SECTION_BLOCK_FORMAT = `
OUTPUT FORMAT (mandatory):
Return ONLY the section body as clean plain text or lightweight markdown (**bold** for role/company lines, • or - for bullets).
No preamble, no "Here is...", no code fences, no [WORK_EXPERIENCE] or [+ SECTION] tags.`.trim();

/** Per-section max output tokens — must be high enough to avoid mid-sentence cutoffs. */
export function resumeSectionMaxTokens(sectionTitle: string): number {
  const t = sectionTitle.toLowerCase();
  if (t.includes('summary') || t.includes('objective')) return 700;
  if (t.includes('experience') || t.includes('employment') || t.includes('career')) return 4000;
  if (t.includes('skill') || t.includes('competenc')) return 1000;
  if (t.includes('education') || t.includes('academic')) return 400;
  if (t.includes('project')) return 900;
  if (t.includes('personal') || t.includes('contact') || t.includes('header')) return 300;
  return 1200;
}

/** Layout-critical generation temperature (Groq 8b / Gemini section passes). */
export const RESUME_LAYOUT_TEMPERATURE = 0.1;

const SECTION_TAG_LINE_RE =
  /^\s*\[+?\s*(?:\+\s*)?(HEADER|PROFESSIONAL_SUMMARY|WORK_EXPERIENCE|TECHNICAL_SKILLS|EDUCATION|PROJECTS|SUMMARY|EXPERIENCE|SKILLS)\s*\]?\s*$/gim;

/** Extract inner body from legacy ```markdown [SECTION] fences without re-entering sanitization. */
function stripFencesOnly(text: string): string {
  const trimmed = text.trim();
  const fenced = trimmed.match(
    /^```(?:markdown)?\s*\[?[+\s]*([A-Z_ \-]+)\]?\s*\n([\s\S]*?)```\s*$/i,
  );
  if (fenced) return fenced[2].trim();
  const inner = trimmed.match(/```(?:markdown)?\s*\[?[+\s]*([A-Z_ \-]+)\]?\s*\n([\s\S]*?)```/i);
  if (inner) return inner[2].trim();
  return trimmed;
}

/**
 * Strip fences, section tags, and stray markup artifacts from LLM section output
 * before canvas render or simulated streaming.
 */
export function sanitizeResumeLlmOutput(text: string): string {
  let t = stripFencesOnly(text ?? '');
  if (!t) return t;

  // Partial / streaming fence fragments
  t = t.replace(/^```(?:markdown)?\s*\[?[+\s]*[A-Z_ \-]+\]?\s*\n?/gim, '');
  t = t.replace(/```(?:markdown)?/gi, '');
  t = t.replace(/\n?```\s*$/g, '');

  // Standalone section tag lines and inline [+ WORK_EXPERIENCE] / [+ Company Name] tokens
  t = t.replace(SECTION_TAG_LINE_RE, '');
  t = t.replace(/\[\+\s*[^\]]+\]/g, '');
  t = t.replace(
    /\[(HEADER|PROFESSIONAL_SUMMARY|WORK_EXPERIENCE|TECHNICAL_SKILLS|EDUCATION|PROJECTS)\]/gi,
    '',
  );
  // Bracket fill-in tokens the model must never leave in final output
  t = t.replace(
    /\[(Company Name|Job Title|Start Date|End Date[^\]]*|Add[^\]]*|Your[^\]]*|Describe[^\]]*|Quantify[^\]]*|Degree Name|Institution Name|Graduation Year[^\]]*)\]/gi,
    '',
  );

  // Stray "markdown" label lines left when fences break mid-stream
  t = t.replace(/^\s*markdown\s*$/gim, '');

  // Collapse contact-line fragments: "Name , Title , Email" → cleaner spacing
  t = t.replace(/\s*,\s*,\s*/g, ' | ');
  t = t.replace(/\s{2,}\|\s{2,}/g, ' | ');

  // Drop leaked agent/UI/instruction fragments and generic marketing taglines
  const junkLinePatterns = [
    /\bprogress made and adopt\b/i,
    /\badopt new changes proposed\b/i,
    /\banother section and after user navigate\b/i,
    /\bnavigate from one page(?:'s)? section\b/i,
    /\buser experience and order accuracy through well\b/i,
    /\bscreen\s*-\s*based workflows\b/i,
    /\bcustomer segments types?\s*\([^)]+\)/i,
    /\binnovative product manager shaping tech solutions\b/i,
  ];
  t = t
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) return true;
      return !junkLinePatterns.some((re) => re.test(trimmed));
    })
    .join('\n');

  return t.replace(/\n{3,}/g, '\n\n').trim();
}

/** Strip optional ```markdown [SECTION] fence from model output before canvas render. */
export function stripResumeSectionFence(text: string): string {
  return sanitizeResumeLlmOutput(text);
}
